import React from 'react';
// Paper removed here to rely on the top-level page-card from Layout
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
// Link removed; use SourceLink component instead
import SourceLink from './SourceLink';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
// CircularProgress removed (unused)
import ModernLoader from './ModernLoader';
import DevicesToolbar from './DevicesToolbar';
import { useDevices } from '../hooks/useDevices';
import { useQueryClient } from '@tanstack/react-query';
import type { UnifiedDeviceInfo, UnifiedDeviceListResponse } from '../types/device';
import { fetchDevices, postDeviceControl, fetchUserDetails, postWirelessMacBatch, postTgmaxxGetGroup, postDeviceCity } from '../services/api';
import { useDeviceCount } from '../contexts/DeviceCountContext';
import { useQuery } from '@tanstack/react-query';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Tooltip from '@mui/material/Tooltip';

export default function DeviceList({ onSelect, page: controlledPage, onPageChange }: { onSelect?: (d: import('../types/device').UnifiedDeviceInfo) => void; page?: number; onPageChange?: (n: number) => void }) {
  const { tgmaxxCount } = useDeviceCount();
  
  // Get user details to access user tenants
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Extract user tenants
  const userTenants = React.useMemo(() => {
    if (!userDetails?.userTenants) return [];
    return userDetails.userTenants.filter((ut: any) => 
      ut?.tenant?.name && (ut.tenant.name.toLowerCase() === 'tgstream' || ut.tenant.name.toLowerCase() === 'tgmaxx' || ut.tenant.name.toLowerCase() === 'tgstreamtest' || ut.tenant.name.toLowerCase() === 'tgstreamtl4kbr')
    );
  }, [userDetails]);
  
  const [internalPage, setInternalPage] = React.useState<number>(controlledPage ?? 1);
  const isControlled = typeof controlledPage === 'number';
  const page = isControlled ? (controlledPage as number) : internalPage;
  React.useEffect(() => { if (controlledPage !== undefined) setInternalPage(controlledPage); }, [controlledPage]);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [snSearch, setSnSearch] = React.useState<string | undefined>(undefined);
  const [macSearch, setMacSearch] = React.useState<string | undefined>(undefined);
  const [wirelessMacMap, setWirelessMacMap] = React.useState<Record<string, string>>({});
  const [wirelessMacLoading, setWirelessMacLoading] = React.useState<Set<string>>(new Set());
  
  // Initialize selectedTenant - will be set when userTenants loads
  const [selectedTenant, setSelectedTenant] = React.useState<string>('');

  // Update selectedTenant when userTenants loads
  React.useEffect(() => {
    if (userTenants.length > 0) {
      // If selectedTenant is empty or not in the list, set to first available tenant
      if (!selectedTenant || !userTenants.find((ut: any) => ut.tenant.name === selectedTenant)) {
        setSelectedTenant(userTenants[0].tenant.name);
      }
    }
  }, [userTenants, selectedTenant]);
  
  // Determine if we're in search mode
  const isSearchMode = Boolean((snSearch && snSearch.trim().length > 0) || (macSearch && macSearch.trim().length > 0));
  
  // For search mode, we want to fetch ALL results, so use a large limit
  const searchLimit = 10000; // Large number to get all results from each tenant
  
  // Fetch data from all tenants when searching, otherwise just the selected tenant
  // Only enable queries when selectedTenant is set (not empty)
  const tgStreamQuery = useDevices({
    page: isSearchMode ? 1 : page, // Always page 1 for search to get all results
    limit: isSearchMode ? searchLimit : pageSize, 
    sn: snSearch,
    mac: macSearch,
    tenant: 'TGStream',
    enabled: selectedTenant !== '' && (isSearchMode || selectedTenant === 'TGStream')
  });
  
  const tgStreamTestQuery = useDevices({
    page: isSearchMode ? 1 : page, // Always page 1 for search to get all results
    limit: isSearchMode ? searchLimit : pageSize, 
    sn: snSearch,
    mac: macSearch,
    tenant: 'TGStreamTest',
    enabled: selectedTenant !== '' && (isSearchMode || selectedTenant === 'TGStreamTest')
  });

    const tgstreamtl4kbrQuery = useDevices({
    page: isSearchMode ? 1 : page, // Always page 1 for search to get all results
    limit: isSearchMode ? searchLimit : pageSize, 
    sn: snSearch,
    mac: macSearch,
    tenant: 'TGStreamTL4KBR',
    enabled: selectedTenant !== '' && (isSearchMode || selectedTenant === 'TGStreamTL4KBR')
  });
  
  const tgMaxxQuery = useDevices({
    page: isSearchMode ? 1 : page, // Always page 1 for search to get all results
    limit: isSearchMode ? searchLimit : pageSize, 
    sn: snSearch,
    mac: macSearch,
    tenant: 'TGMaxx',
    enabled: selectedTenant !== '' && (isSearchMode || selectedTenant === 'TGMaxx')
  });
  
  // Use combined data in search mode, otherwise use selected tenant data
  const data = React.useMemo(() => {
    if (isSearchMode) {
      // Combine results from all tenants
      const tgStreamDevices = tgStreamQuery.data?.devices || [];
      // const tgStreamTestDevices = tgStreamTestQuery.data?.devices || [];
      const tgMaxxDevices = tgMaxxQuery.data?.devices || [];
      // const allCombinedDevices = [...tgStreamDevices, ...tgStreamTestDevices, ...tgMaxxDevices];
      const allCombinedDevices = [...tgStreamDevices, ...tgMaxxDevices];
      
      // Sort combined results by relevance
      allCombinedDevices.sort((a, b) => {
        // Sort by online status first (online devices first)
        if (a.online !== b.online) return a.online ? -1 : 1;
        // Then by activation status
        if (a.activate !== b.activate) return a.activate ? -1 : 1;
        // Then by serial number alphabetically
        return (a.sn || '').localeCompare(b.sn || '');
      });
      
      // Apply client-side pagination to combined results
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedDevices = allCombinedDevices.slice(startIndex, endIndex);
      
      return {
        devices: paginatedDevices,
        total: allCombinedDevices.length, // Total count of all combined results
        allDevices: allCombinedDevices // Keep reference to all devices for pagination
      };
    } else {
      // Use selected tenant data - backend should provide correct source
      return selectedTenant === 'TGStream' ? tgStreamQuery.data : 
             selectedTenant === 'TGStreamTest' ? tgStreamTestQuery.data :
             selectedTenant === 'TGStreamTL4KBR' ? tgstreamtl4kbrQuery.data :
             tgMaxxQuery.data;
    }
  }, [isSearchMode, tgStreamQuery.data, tgStreamTestQuery.data, tgstreamtl4kbrQuery.data, tgMaxxQuery.data, selectedTenant, page, pageSize]);
  
  const isLoading = isSearchMode 
    ? (tgStreamQuery.isLoading || tgStreamTestQuery.isLoading || tgstreamtl4kbrQuery.isLoading || tgMaxxQuery.isLoading)
    : (selectedTenant === 'TGStream' ? tgStreamQuery.isLoading : 
       selectedTenant === 'TGStreamTest' ? tgStreamTestQuery.isLoading :
       selectedTenant === 'TGStreamTL4KBR' ? tgstreamtl4kbrQuery.isLoading :
       tgMaxxQuery.isLoading);
    
  const isError = isSearchMode 
    ? (tgStreamQuery.isError && tgStreamTestQuery.isError && tgstreamtl4kbrQuery.isError && tgMaxxQuery.isError)
    : (selectedTenant === 'TGStream' ? tgStreamQuery.isError : 
       selectedTenant === 'TGStreamTest' ? tgStreamTestQuery.isError :
       selectedTenant === 'TGStreamTL4KBR' ? tgstreamtl4kbrQuery.isError :
       tgMaxxQuery.isError);
    
  const error = isSearchMode 
    ? (tgStreamQuery.error || tgStreamTestQuery.error || tgstreamtl4kbrQuery.error || tgMaxxQuery.error)
    : (selectedTenant === 'TGStream' ? tgStreamQuery.error : 
       selectedTenant === 'TGStreamTest' ? tgStreamTestQuery.error :
       selectedTenant === 'TGStreamTL4KBR' ? tgstreamtl4kbrQuery.error :
       tgMaxxQuery.error);
    
  const isFetching = isSearchMode 
    ? (tgStreamQuery.isFetching || tgStreamTestQuery.isFetching || tgstreamtl4kbrQuery.isFetching || tgMaxxQuery.isFetching)
    : (selectedTenant === 'TGStream' ? tgStreamQuery.isFetching : 
       selectedTenant === 'TGStreamTest' ? tgStreamTestQuery.isFetching :
       selectedTenant === 'TGStreamTL4KBR' ? tgstreamtl4kbrQuery.isFetching :
       tgMaxxQuery.isFetching);
    
  const refetch = () => {
    if (isSearchMode) {
      tgStreamQuery.refetch();
      tgStreamTestQuery.refetch();
      tgstreamtl4kbrQuery.refetch();
      tgMaxxQuery.refetch();
    } else {
      if (selectedTenant === 'TGStream') {
        tgStreamQuery.refetch();
      } else if (selectedTenant === 'TGStreamTest') {
        tgStreamTestQuery.refetch();
      } else if (selectedTenant === 'TGStreamTL4KBR') {
        tgstreamtl4kbrQuery.refetch();
      } else {
        tgMaxxQuery.refetch();
      }
    }
  };
  
  const queryClient = useQueryClient();
  const [validating, setValidating] = React.useState(false);
  const [pageInput, setPageInput] = React.useState<string>(String(page));
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [snack, setSnack] = React.useState<{ open: boolean; message: string; severity: 'success'|'error' }>({ open: false, message: '', severity: 'success' });

  const handlePageChange = React.useCallback(
    async (newPage: number) => {
      const target = Math.max(1, newPage);
      if (target === page) return;

      // Optimistically update the visible page so the Pagination control highlights immediately.
      if (isControlled) {
        onPageChange?.(target);
      } else {
        setInternalPage(target);
      }

      // In search mode, we don't need to prefetch since we have all data client-side
      if (isSearchMode) {
        // Data will be re-paginated automatically by the useMemo above
        return;
      }

      setValidating(true);
      try {
        // Prefetch selected tenant only for normal mode
        await queryClient.prefetchQuery({ 
          queryKey: ['devices', target, pageSize, undefined, macSearch, snSearch, selectedTenant], 
          queryFn: () => fetchDevices({ page: target, limit: pageSize, mac: macSearch, sn: snSearch, tenant: selectedTenant }) 
        });
      } catch (err) {
        console.error('Page validation failed', err);
      } finally {
        setValidating(false);
      }
    },
    [pageSize, page, isControlled, onPageChange, queryClient, macSearch, snSearch, selectedTenant, isSearchMode]
  );

  // Handle search from toolbar. Reset to page 1 when search changes.
  const handleSearchSn = React.useCallback((q?: string) => {
    const v = q && q.trim() ? q.trim() : undefined;
    setSnSearch(v);
    if (v) {
      setMacSearch(undefined);
    }
    if (isControlled) {
      onPageChange?.(1);
    } else {
      setInternalPage(1);
    }
  }, [isControlled, onPageChange]);

  const handleSearchMac = React.useCallback((q?: string) => {
    const v = q && q.trim() ? q.trim() : undefined;
    setMacSearch(v);
    if (v) {
      setSnSearch(undefined);
    }
    if (isControlled) {
      onPageChange?.(1);
    } else {
      setInternalPage(1);
    }
  }, [isControlled, onPageChange]);

  // Handle tenant change. Reset to page 1 when tenant changes.
  // Don't allow tenant changes during search mode
  const handleTenantChange = React.useCallback((event: React.SyntheticEvent, newValue: string) => {
    if (isSearchMode) return; // Prevent tenant changes during search
    
    setSelectedTenant(newValue);
    if (isControlled) {
      onPageChange?.(1);
    } else {
      setInternalPage(1);
    }
  }, [isControlled, onPageChange, isSearchMode]);

  const typedData = data as UnifiedDeviceListResponse | undefined;
  const pageDevices = React.useMemo(() => typedData?.devices ?? [], [typedData?.devices]);
  const tgMaxxOnlineSns = React.useMemo(() => {
    const sns = pageDevices
      .filter((d) => (d.source ?? '').toLowerCase() === 'tgmaxx' && d.online && d.sn)
      .map((d) => d.sn as string);
    return Array.from(new Set(sns));
  }, [pageDevices]);
  const wirelessMacRequestKey = React.useMemo(() => tgMaxxOnlineSns.join('|'), [tgMaxxOnlineSns]);
  const wirelessMacRequestRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!wirelessMacRequestKey) {
      setWirelessMacMap({});
      setWirelessMacLoading(new Set());
      wirelessMacRequestRef.current = '';
      return;
    }
    if (wirelessMacRequestRef.current === wirelessMacRequestKey) return;
    wirelessMacRequestRef.current = wirelessMacRequestKey;
    setWirelessMacLoading(new Set(tgMaxxOnlineSns));
    setWirelessMacMap({});
    (async () => {
      try {
        const res = await postWirelessMacBatch({ sns: tgMaxxOnlineSns });
        setWirelessMacMap(res?.data ?? {});
      } catch (err) {
        console.error('Failed to fetch wireless MAC batch', err);
        setWirelessMacMap({});
      } finally {
        setWirelessMacLoading(new Set());
      }
    })();
  }, [wirelessMacRequestKey, tgMaxxOnlineSns]);
  // Per-device async group lookup for TGMaxx devices
  const [groupMap, setGroupMap] = React.useState<Record<string, string>>({});
  const [groupLoading, setGroupLoading] = React.useState<Set<string>>(new Set());
  const groupRequestedRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const targets = pageDevices.filter((d) => (d.source ?? '').toLowerCase() === 'tgmaxx' && (d.sn || d.mac));
    const pending = targets.filter((d) => !groupRequestedRef.current.has(d.id));
    if (pending.length === 0) return;
    pending.forEach((d) => groupRequestedRef.current.add(d.id));
    setGroupLoading((prev) => {
      const next = new Set(prev);
      pending.forEach((d) => next.add(d.id));
      return next;
    });
    pending.forEach((d) => {
      (async () => {
        try {
          const res = await postTgmaxxGetGroup({ sn: d.sn, mac: d.mac });
          const name = res?.data?.groupName;
          if (name) setGroupMap((prev) => ({ ...prev, [d.id]: name }));
        } catch (err) {
          console.error('Failed to fetch group for device', d.sn ?? d.mac, err);
          groupRequestedRef.current.delete(d.id);
        } finally {
          setGroupLoading((prev) => {
            const next = new Set(prev);
            next.delete(d.id);
            return next;
          });
        }
      })();
    });
  }, [pageDevices]);

  // Per-device async city lookup (all sources); does not block the device listing
  const [cityMap, setCityMap] = React.useState<Record<string, string>>({});
  const [cityLoading, setCityLoading] = React.useState<Set<string>>(new Set());
  const cityRequestedRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const pending = pageDevices.filter((d) => d.sn && !cityRequestedRef.current.has(d.id));
    if (pending.length === 0) return;
    pending.forEach((d) => cityRequestedRef.current.add(d.id));
    setCityLoading((prev) => {
      const next = new Set(prev);
      pending.forEach((d) => next.add(d.id));
      return next;
    });
    pending.forEach((d) => {
      (async () => {
        try {
          const res = await postDeviceCity(d.source, { sn: d.sn as string });
          if (res?.city) setCityMap((prev) => ({ ...prev, [d.id]: res.city as string }));
        } catch (err) {
          console.error('Failed to fetch city for device', d.sn, err);
          cityRequestedRef.current.delete(d.id);
        } finally {
          setCityLoading((prev) => {
            const next = new Set(prev);
            next.delete(d.id);
            return next;
          });
        }
      })();
    });
  }, [pageDevices]);

  // Remember last known server total so transient loading states don't look like "total = 0".
  const lastTotalRef = React.useRef<number | null>(null);
  // Prefer explicit server-provided total when available. If not available, fall back to
  // the last observed total while a new page is being fetched. If we have device list
  // for the current page and server didn't include total, use its length as a fallback.
  const serverTotal = typeof typedData?.total === 'number' ? typedData.total : undefined;
  React.useEffect(() => { if (typeof typedData?.total === 'number') lastTotalRef.current = typedData!.total; }, [typedData]);
  
  // For search mode, we might not have accurate pagination since we're combining results
  // But now we do client-side pagination, so we can show proper totals
  // For TGMaxx, use stored count from dashboard if available
  const totalItems = isSearchMode 
    ? (typedData?.total || 0) // Use total count of all combined results
    : selectedTenant === 'TGMaxx' && tgmaxxCount !== null
      ? tgmaxxCount // Use stored count from dashboard for TGMaxx
      : (serverTotal ?? (lastTotalRef.current ?? (Array.isArray(typedData?.devices) ? typedData!.devices.length : 0)));
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize)); // Proper pagination for both modes

  // If pageSize or totalItems changes such that current page is out of range, clamp it
  React.useEffect(() => {
    if (page > totalPages) {
      // navigate to last page
      void handlePageChange(totalPages);
    }
  // keep the input synced with current page
  setPageInput(String(page));
  }, [pageSize, totalItems, totalPages, handlePageChange, page]);
  
  // Define available columns and visible columns state (removed sorting columns)
  const allColumns = React.useMemo(() => [
    { key: 'sn', label: 'SN', width: '18%', align: 'left' },
    { key: 'software', label: 'Build', width: '8%', align: 'left' },
    { key: 'mac', label: 'MAC', width: '16%', align: 'left' },
    { key: 'wirelessMac', label: 'Wireless MAC', width: '16%', align: 'left' },
    { key: 'groups', label: 'Groups', width: '14%', align: 'left' },
    { key: 'city', label: 'City', width: '10%', align: 'left' },
    { key: 'online', label: 'Online', width: '8%', align: 'left' },
    { key: 'activate', label: 'Activated', width: '8%', align: 'left' },
    { key: 'source', label: 'Model', width: '10%', align: 'left' },
    { key: 'actions', label: 'Actions', width: '13%', align: 'left' },
  ], []);
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>(allColumns.map((c) => c.key));
  const handleToggleColumn = React.useCallback((k: string) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(k);
      if (isVisible) {
        // Prevent removing the last visible column
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== k);
      }
      return [...prev, k];
    });
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" className="page-title">
          {isSearchMode ? `Devices - Search Results` : 'Devices'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSearchMode ? `Found: ${totalItems}` : selectedTenant === 'TGMaxx' && tgmaxxCount !== null 
            ? `Total: ${tgmaxxCount}` 
            : `Total: ${typedData?.total ?? '—'}`}
        </Typography>
      </Box>

      <Paper sx={{ p: 1, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)' }} elevation={3}>
        {/* Tenant Tabs */}
        {selectedTenant !== '' && userTenants.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={selectedTenant} 
              onChange={handleTenantChange} 
              aria-label="tenant tabs"
              sx={{
                '& .MuiTab-root': {
                  opacity: isSearchMode ? 0.5 : 1,
                  pointerEvents: isSearchMode ? 'none' : 'auto',
                  cursor: isSearchMode ? 'not-allowed' : 'pointer'
                }
              }}
            >
              {userTenants.map((ut: any) => (
                <Tab 
                  key={ut.tenant.name} 
                  label={isSearchMode 
                    ? `${ut.tenant.displayName || ut.tenant.name} (Search Mode)` 
                    : (ut.tenant.displayName || ut.tenant.name)}
                  value={ut.tenant.name}
                  disabled={isSearchMode}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 600,
                    color: isSearchMode ? 'text.disabled' : 'inherit'
                  }}
                />
              ))}
            </Tabs>
            {isSearchMode && (
              <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
            >
              Showing combined results from all tenants
            </Typography>
            )}
          </Box>
        )}

        <DevicesToolbar
          snValue={snSearch}
          macValue={macSearch}
          onSearchSn={(q) => handleSearchSn(q)}
          onSearchMac={(q) => handleSearchMac(q)}
          onRefresh={() => void refetch()}
          availableColumns={allColumns.map((c) => ({ key: c.key, label: c.label }))}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
        />

      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {allColumns.filter((c) => visibleColumns.includes(c.key)).map((c) => (
                <TableCell key={c.key} sx={{ width: c.width }} align={(c as any).align ?? 'left'}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Show different body states but always keep table head visible so users can click sort repeatedly */}
            {isError ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} sx={{ py: 4 }}>
                    <Typography color="error">Error loading devices: {error?.message}</Typography>
                  </TableCell>
                </TableRow>
              ) : isLoading && !(typedData && typedData.devices && typedData.devices.length) ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} sx={{ py: 4, textAlign: 'center' }}>
                    <ModernLoader size={36} />
                  </TableCell>
                </TableRow>
              ) : (typedData?.devices ?? []).map((d: UnifiedDeviceInfo) => (
              <TableRow key={d.id} hover onClick={(e) => {
                try {
                  const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection()?.toString().trim() : '';
                  if (sel) return;
                } catch (err) {
                  // ignore selection read errors and proceed with click
                }
                const resolvedGroup = (d.source ?? '').toLowerCase() === 'tgmaxx' ? groupMap[d.id] : undefined;
                onSelect?.(resolvedGroup ? { ...d, groups: [resolvedGroup] } : d);
              }} sx={{ cursor: 'pointer' }}>
                {allColumns.filter((c) => visibleColumns.includes(c.key)).map((c) => {
                  switch (c.key) {
                    case 'sn':
                      return <TableCell key={`${d.id}-sn`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.sn}</TableCell>;
                    case 'software':
                      return <TableCell key={`${d.id}-sw`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.software}</TableCell>;
                    case 'mac':
                      return <TableCell key={`${d.id}-mac`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.mac ?? '\u2014'}</TableCell>;
                    case 'wirelessMac': {
                      const isTgMaxx = (d.source ?? '').toLowerCase() === 'tgmaxx';
                      const sn = d.sn ?? '';
                      const isLoadingWireless = isTgMaxx && d.online && sn && wirelessMacLoading.has(sn) && wirelessMacMap[sn] === undefined;
                      const wirelessValue = isTgMaxx && sn && wirelessMacMap[sn] !== undefined
                        ? wirelessMacMap[sn]
                        : d.wirelessMac;
                      return (
                        <TableCell key={`${d.id}-wirelessMac`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {isLoadingWireless ? (
                            <CircularProgress size={14} thickness={5} />
                          ) : (wirelessValue ? wirelessValue : '\u2014')}
                        </TableCell>
                      );
                    }
                    case 'city': {
                      const cityVal = cityMap[d.id] ?? d.city;
                      const cLoading = cityLoading.has(d.id) && !cityVal;
                      return (
                        <TableCell key={`${d.id}-city`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {cLoading ? <CircularProgress size={14} thickness={5} /> : (cityVal || '\u2014')}
                        </TableCell>
                      );
                    }
                    case 'groups': {
                      const isTgMaxxG = (d.source ?? '').toLowerCase() === 'tgmaxx';
                      const gName = isTgMaxxG ? groupMap[d.id] : undefined;
                      const gLoading = isTgMaxxG && groupLoading.has(d.id) && !gName;
                      return (
                        <TableCell key={`${d.id}-groups`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {gLoading ? <CircularProgress size={14} thickness={5} /> : (gName ?? (d.groups && d.groups.length ? d.groups.join(', ') : '\u2014'))}
                        </TableCell>
                      );
                    }
                    case 'online':
                      return <TableCell key={`${d.id}-online`}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span className={`app-status-bubble ${d.online ? 'status-green' : 'status-grey'}`} /></Box></TableCell>;
                    case 'activate':
                      return <TableCell key={`${d.id}-activate`}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span className={`app-status-bubble ${d.activate ? 'status-green' : 'status-grey'}`} /></Box></TableCell>;
                    case 'source':
                      return <TableCell key={`${d.id}-src`} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <SourceLink source={d.source} onClick={(e) => e.stopPropagation()} />
                      </TableCell>;
                    case 'actions':
                      return (
                        <TableCell key={`${d.id}-actions`} align="left" sx={{ width: '4%' }}>
                          <Tooltip title="Lock">
                            <IconButton disabled={Boolean(actionLoading)} size="small" aria-label="lock" onClick={async (e) => {
                            e.stopPropagation();
                            const cmd = 'LOCK';
                            setActionLoading(`${d.id}-${cmd}`);
                            try {
                              await postDeviceControl({ source: d.source, deviceId: d.sn ?? d.id, deviceCommandEnum: cmd });
                              setSnack({ open: true, message: 'Lock command sent', severity: 'success' });
                            } catch (err: any) {
                              const msg = err?.response?.data?.message ?? err?.message ?? String(err);
                              setSnack({ open: true, message: `Lock failed: ${msg}`, severity: 'error' });
                            } finally { setActionLoading(null); }
                          }}>
                              <LockIcon fontSize="small" sx={{ color: '#e57373' }} />
                            </IconButton>
                          </Tooltip>

          <Tooltip title="Unlock">
        <IconButton disabled={Boolean(actionLoading)} size="small" aria-label="unlock" onClick={async (e) => {
                            e.stopPropagation();
                            const cmd = 'UNLOCK';
                            setActionLoading(`${d.id}-${cmd}`);
                            try {
                              await postDeviceControl({ source: d.source, deviceId: d.sn ?? d.id, deviceCommandEnum: cmd });
                              setSnack({ open: true, message: 'Unlock command sent', severity: 'success' });
                            } catch (err: any) {
                              const msg = err?.response?.data?.message ?? err?.message ?? String(err);
                              setSnack({ open: true, message: `Unlock failed: ${msg}`, severity: 'error' });
                            } finally { setActionLoading(null); }
                          }}>
                              <LockOpenIcon fontSize="small" sx={{ color: '#66bb6a' }} />
                            </IconButton>
                          </Tooltip>

          <Tooltip title="Reboot">
        <IconButton disabled={Boolean(actionLoading)} size="small" aria-label="reboot" onClick={async (e) => {
                            e.stopPropagation();
                            const cmd = 'REBOOT';
                            setActionLoading(`${d.id}-${cmd}`);
                            try {
                              await postDeviceControl({ source: d.source, deviceId: d.sn ?? d.id, deviceCommandEnum: cmd });
                              setSnack({ open: true, message: 'Reboot command sent', severity: 'success' });
                            } catch (err: any) {
                              const msg = err?.response?.data?.message ?? err?.message ?? String(err);
                              setSnack({ open: true, message: `Reboot failed: ${msg}`, severity: 'error' });
                            } finally { setActionLoading(null); }
                          }}>
                              <RestartAltIcon fontSize="small" sx={{ color: '#ffb74d' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      );
                    default:
                      return <TableCell key={`${d.id}-${c.key}`}>{'\u2014'}</TableCell>;
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="devices-per-page-label">Per page</InputLabel>
            <Select
              labelId="devices-per-page-label"
              value={pageSize}
              label="Per page"
              onChange={(e) => {
                const raw = (e.target as EventTarget & { value?: unknown })?.value as string | undefined;
                const v = Number(raw ?? '') || 20;
                setPageSize(v);
                // reset to first page when page size changes
                void handlePageChange(1);
              }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {isSearchMode 
              ? `Found: ${totalItems} results (showing ${typedData?.devices?.length || 0} per page)` 
              : selectedTenant === 'TGMaxx' && tgmaxxCount !== null
                ? `Total: ${tgmaxxCount}`
                : `Total: ${totalItems}`
            }
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Pagination count={totalPages} page={page} onChange={(_e, v) => void handlePageChange(v)} color="primary" />
          <TextField
            size="small"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={() => {
              const v = Number(pageInput) || 1;
              const target = Math.max(1, Math.min(totalPages, Math.floor(v)));
              if (target !== page) void handlePageChange(target);
              setPageInput(String(target));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = Number(pageInput) || 1;
                const target = Math.max(1, Math.min(totalPages, Math.floor(v)));
                if (target !== page) void handlePageChange(target);
                setPageInput(String(target));
              }
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center', width: 56 } }}
            aria-label="Go to page"
          />
          {isFetching || validating ? <ModernLoader size={18} /> : null}
        </Box>
      </Box>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
  </Paper>
    </>
  );
}
